import React, { useState } from 'react';
import { X, Plus } from 'lucide-react';
import Modal from '../../../../components/ui/Modal';
import type { AiPodPlanDto, CreatePlanPayload, FeatureMeta } from '../../../../services/aiPodBillingService';
import { Button } from '../../../../components/ui/Button';
import { SelectField, TextArea, TextField } from '../../../../components/ui/Field';

/** A feature on the plan, with its value for each billing interval the plan is sold at. */
export type PlanItem = { featureId: string; feature: FeatureMeta; values: Record<string, any> };
/** What is saved: one value per feature per billing interval. */
export type PlanFeatureValueRow = { featureId: string; interval: string; value: any };

const INTERVAL_LABEL: Record<string, string> = { month: 'Monthly', year: 'Yearly' };

// `amount` is the price exactly as typed (major units, e.g. "29.50"). It is only
// converted to minor units on save — reformatting it on every keystroke would
// rewrite the field under the cursor.
type PricingRow = {
  amount: string;
  currency: string;
  interval: string;
};

const minorToAmount = (minor: number): string => (minor / 100).toFixed(2);

// Digits with an optional decimal part of up to two places.
const AMOUNT_INPUT = /^\d*(\.\d{0,2})?$/;

type Props = {
  plan?: AiPodPlanDto | null;
  allFeatures: FeatureMeta[];
  initialItems?: PlanItem[];
  onClose: () => void;
  onSave: (payload: CreatePlanPayload, rows: PlanFeatureValueRow[]) => Promise<void>;
};

const INTERVALS = ['month', 'year'];
const CURRENCIES = ['usd', 'eur', 'gbp', 'cad', 'aud'];

const labelCls = 'block text-xs font-semibold text-agentic-text-secondary mb-1.5 uppercase tracking-wide';
const inputCls =
  'w-full rounded-lg border border-agentic-gray-300 bg-white px-3 py-2 text-sm text-agentic-text-primary placeholder-agentic-text-muted focus:border-kv-agentic-blue focus:outline-none focus:ring-2 focus:ring-kv-agentic-blue/20';
const selectCls =
  'w-full rounded-lg border border-agentic-gray-300 bg-white px-3 py-2 text-sm text-agentic-text-primary focus:border-kv-agentic-blue focus:outline-none focus:ring-2 focus:ring-kv-agentic-blue/20';

/** Mirrors the server's check so problems show before saving. */
function featureValueProblem(feature: FeatureMeta, value: any): string | null {
  if (feature.type === 'boolean') return typeof value === 'boolean' ? null : 'must be on or off';
  if (feature.type === 'limit') {
    if (typeof value !== 'number' || !Number.isInteger(value)) return 'needs a whole number';
    if (value === -1) return feature.unlimitedAllowed ? null : 'cannot be unlimited';
    if (value < 0) return 'cannot be negative';
    if (feature.minValue != null && value < feature.minValue) return `must be at least ${feature.minValue}`;
    if (feature.maxValue != null && value > feature.maxValue) return `must be at most ${feature.maxValue}`;
    return null;
  }
  if (feature.type === 'option') return (feature.options ?? []).some((o) => o.value === value) ? null : 'needs an option';
  return null;
}

function ValueInput({ feature, value, onChange }: { feature: FeatureMeta; value: any; onChange: (v: any) => void }) {
  if (feature.type === 'boolean') {
    const checked = value === true || value === 'true';
    return (
      <div
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-5 w-9 cursor-pointer items-center rounded-full transition-colors ${checked ? 'bg-kv-agentic-blue' : 'bg-agentic-gray-300'}`}
      >
        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : 'translate-x-1'}`} />
      </div>
    );
  }
  if (feature.type === 'limit') {
    if (feature.unlimitedAllowed && (value === -1 || value === '-1')) {
      return (
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-agentic-text-primary">Unlimited</span>
          <button type="button" className="text-xs text-kv-agentic-blue underline" onClick={() => onChange(0)}>
            Set a limit
          </button>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-2">
      <TextField
        type="number"
        min={0}
        className="w-24 rounded-lg border border-agentic-gray-300 px-2 py-1.5 text-sm focus:border-kv-agentic-blue focus:outline-none focus:ring-2 focus:ring-kv-agentic-blue/20"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
      />
      {feature.unlimitedAllowed && (
        <button type="button" className="text-xs text-kv-agentic-blue underline" onClick={() => onChange(-1)}>
          Unlimited
        </button>
      )}
      </div>
    );
  }
  if (feature.type === 'option') {
    return (
      <SelectField
        className="rounded-lg border border-agentic-gray-300 px-2 py-1.5 text-sm focus:border-kv-agentic-blue focus:outline-none focus:ring-2 focus:ring-kv-agentic-blue/20"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">— select —</option>
        {(feature.options ?? []).map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </SelectField>
    );
  }
  return null;
}

const typeBadge: Record<string, string> = {
  boolean: 'bg-purple-50 text-purple-700 ring-1 ring-purple-200',
  limit: 'bg-blue-50 text-blue-700 ring-1 ring-blue-200',
  option: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
};

export const PlanModal: React.FC<Props> = ({ plan, allFeatures, initialItems = [], onClose, onSave }) => {
  const isEdit = !!plan;

  const [form, setForm] = useState({
    planKey: plan?.planKey ?? '',
    displayName: plan?.displayName ?? '',
    description: plan?.description ?? '',
  });

  const [pricingRows, setPricingRows] = useState<PricingRow[]>(() => {
    const intervals = (plan as any)?.pricingIntervals as Array<{ interval: string; amountMinor: number; currency: string }> | undefined;
    if (intervals && intervals.length > 0) {
      return intervals.map((pi) => ({
        amount: minorToAmount(pi.amountMinor),
        currency: pi.currency,
        interval: pi.interval,
      }));
    }
    return [{
      amount: plan?.amountMinor != null ? minorToAmount(plan.amountMinor) : '',
      currency: plan?.currency ?? 'usd',
      interval: plan?.interval ?? 'month',
    }];
  });

  // A new plan starts with every active feature at its default, so nothing the runtime
  // enforces is silently left at "not included".
  const [items, setItems] = useState<PlanItem[]>(() =>
    isEdit || initialItems.length > 0
      ? initialItems
      : allFeatures.map((f) => ({ featureId: f.id, feature: f, values: { month: f.defaultValue ?? (f.type === 'boolean' ? false : f.type === 'limit' ? 0 : '') } }))
  );
  const [trialDays, setTrialDays] = useState<string>(plan?.trialDays ? String(plan.trialDays) : '0');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showFeaturePicker, setShowFeaturePicker] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');

  const set = (k: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      const value = e.target.value;
      setForm((f) => {
        const next = { ...f, [k]: value };
        // Auto-derive plan key from display name on create
        if (!isEdit && k === 'displayName') {
          next.planKey = value.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
        }
        return next;
      });
    };

  const setPricingField = (idx: number, k: keyof PricingRow, value: string) =>
    setPricingRows((rows) => rows.map((r, i) => (i === idx ? { ...r, [k]: value } : r)));

  const addPricingRow = () =>
    setPricingRows((rows) => {
      const usedIntervals = rows.map((r) => r.interval);
      const nextInterval = INTERVALS.find((i) => !usedIntervals.includes(i)) ?? 'year';
      return [...rows, { amount: '', currency: rows[0]?.currency ?? 'usd', interval: nextInterval }];
    });

  const removePricingRow = (idx: number) =>
    setPricingRows((rows) => rows.filter((_, i) => i !== idx));

  // The intervals this plan is sold at, in the order of its pricing rows.
  const intervals = [...new Set(pricingRows.map((r) => r.interval))];

  // A feature's value for an interval. One not set yet (an interval just added) starts
  // from the plan's first interval, so adding yearly pricing doesn't reset everything.
  const valueFor = (item: PlanItem, interval: string) => {
    if (interval in item.values) return item.values[interval];
    const first = Object.keys(item.values).find((k) => intervals.includes(k)) ?? Object.keys(item.values)[0];
    return first !== undefined ? item.values[first] : undefined;
  };

  const setItemValue = (featureId: string, interval: string, value: any) =>
    setItems((prev) => prev.map((i) => (i.featureId === featureId ? { ...i, values: { ...i.values, [interval]: value } } : i)));

  const addFeature = (feature: FeatureMeta) => {
    const defaultVal = feature.type === 'boolean' ? false : feature.type === 'limit' ? 0 : (feature.options?.[0]?.value ?? '');
    setItems((prev) => [...prev, { featureId: feature.id, feature, values: Object.fromEntries(intervals.map((i) => [i, defaultVal])) }]);
    setShowFeaturePicker(false);
    setPickerSearch('');
  };

  const pickerOptions = allFeatures.filter(
    (f) =>
      !items.some((i) => i.featureId === f.id) &&
      (f.name.toLowerCase().includes(pickerSearch.toLowerCase()) || f.key.toLowerCase().includes(pickerSearch.toLowerCase()))
  );

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setError('');
    if (!form.planKey.trim()) return setError('Plan key is required');
    if (!form.displayName.trim()) return setError('Display name is required');

    if (pricingRows.length === 0) return setError('At least one pricing interval is required');
    const intervals = pricingRows.map((r) => r.interval);
    if (new Set(intervals).size !== intervals.length) return setError('Each pricing row must have a unique interval');

    const pricingIntervals: Array<{ interval: string; amountMinor: number; currency: string }> = [];
    for (let i = 0; i < pricingRows.length; i++) {
      const row = pricingRows[i];
      const major = Number(row.amount);
      if (row.amount.trim() === '' || row.amount === '.' || !Number.isFinite(major) || major < 0)
        return setError(`Pricing row ${i + 1}: enter a valid price (e.g. 29.00)`);
      pricingIntervals.push({ interval: row.interval, amountMinor: Math.round(major * 100), currency: row.currency });
    }

    const trial = Number(trialDays || '0');
    if (!Number.isInteger(trial) || trial < 0 || trial > 365) return setError('Trial must be 0–365 whole days');
    const allFree = pricingIntervals.every((p) => p.amountMinor === 0);
    if (allFree && trial > 0) return setError('A free plan has no trial — set the trial to 0');

    const rows: PlanFeatureValueRow[] = [];
    for (const item of items) {
      for (const interval of intervals) {
        const value = valueFor(item, interval);
        const problem = featureValueProblem(item.feature, value);
        if (problem) {
          const which = intervals.length > 1 ? ` (${(INTERVAL_LABEL[interval] ?? interval).toLowerCase()})` : '';
          return setError(`${item.feature.name}${which} ${problem}`);
        }
        rows.push({ featureId: item.featureId, interval, value });
      }
    }

    const payload: CreatePlanPayload = {
      planKey: form.planKey.trim(),
      billingMode: 'SUBSCRIPTION',
      displayName: form.displayName.trim(),
      description: form.description.trim() || undefined,
      pricingIntervals,
      trialDays: trial,
    };

    setSaving(true);
    try {
      await onSave(payload, rows);
      onClose();
    } catch (err: any) {
      setError(err?.message ?? 'Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Modal
        isOpen
        onClose={onClose}
        title={isEdit ? 'Edit Plan' : 'New Plan'}
        size="lg"
        footer={
          <>
            <Button tone="custom"
              type="button"
              onClick={onClose}
              className="rounded-lg border border-agentic-gray-300 px-4 py-2 text-sm font-medium text-agentic-text-secondary hover:bg-agentic-gray-50 transition-colors"
            >
              Cancel
            </Button>
            <Button tone="primary-gradient"
              type="button"
              onClick={() => handleSubmit()}
              disabled={saving}
              className="rounded-lg px-4 py-2 text-sm font-semibold transition-all disabled:opacity-60"
            >
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Plan'}
            </Button>
          </>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Plan key + display name */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Plan Key</label>
              <TextField
                className={inputCls}
                placeholder="e.g. starter"
                value={form.planKey}
                onChange={set('planKey')}
                disabled={isEdit}
              />
              {isEdit && (
                <p className="mt-1 text-xs text-agentic-text-muted">Key is locked after creation</p>
              )}
            </div>
            <div>
              <label className={labelCls}>Display Name</label>
              <TextField
                className={inputCls}
                placeholder="e.g. Starter"
                value={form.displayName}
                onChange={set('displayName')}
              />
            </div>
          </div>

          <div>
            <label className={labelCls}>Description</label>
            <TextArea
              className={`${inputCls} resize-none`}
              rows={2}
              placeholder="Optional description"
              value={form.description}
              onChange={set('description')}
            />
          </div>

          {/* Pricing rows */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className={labelCls} style={{ marginBottom: 0 }}>Pricing</label>
              <Button tone="primary-gradient"
                type="button"
                onClick={addPricingRow}
                disabled={pricingRows.length >= 2}
                className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium transition-all disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Plus className="h-3 w-3" />
                Add interval
              </Button>
            </div>

            <div className="space-y-2">
              {pricingRows.map((row, idx) => (
                <div key={idx} className="grid grid-cols-[1fr_140px_140px_32px] items-end gap-3 rounded-lg border border-agentic-gray-200 bg-agentic-gray-50 px-4 py-3">
                  <div>
                    <label className={labelCls}>Price ($)</label>
                    <div className="flex items-center gap-1.5 rounded-lg border border-agentic-gray-300 bg-white px-3 py-2 focus-within:border-kv-agentic-blue focus-within:ring-2 focus-within:ring-kv-agentic-blue/20">
                      <span className="text-sm text-agentic-text-muted">$</span>
                      <TextField
                        className="w-full bg-transparent text-sm text-agentic-text-primary placeholder-agentic-text-muted focus:outline-none"
                        type="text"
                        inputMode="decimal"
                        placeholder="0.00"
                        value={row.amount}
                        onChange={(e) => {
                          if (AMOUNT_INPUT.test(e.target.value)) setPricingField(idx, 'amount', e.target.value);
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Currency</label>
                    <SelectField className={selectCls} value={row.currency} onChange={(e) => setPricingField(idx, 'currency', e.target.value)}>
                      {CURRENCIES.map((c) => (
                        <option key={c} value={c}>{c.toUpperCase()}</option>
                      ))}
                    </SelectField>
                  </div>
                  <div>
                    <label className={labelCls}>Interval</label>
                    <SelectField className={selectCls} value={row.interval} onChange={(e) => setPricingField(idx, 'interval', e.target.value)}>
                      {INTERVALS.filter((i) => i === row.interval || !pricingRows.some((r, ri) => ri !== idx && r.interval === i)).map((i) => (
                        <option key={i} value={i}>{i.charAt(0).toUpperCase() + i.slice(1)}</option>
                      ))}
                    </SelectField>
                  </div>
                  <div className="flex items-end pb-0.5">
                    {pricingRows.length > 1 ? (
                      <Button tone="danger-ghost"
                        type="button"
                        onClick={() => removePricingRow(idx)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors"
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    ) : (
                      <div className="h-8 w-8" />
                    )}
                  </div>
                </div>
              ))}
            </div>

            {pricingRows.length > 1 && (
              <p className="mt-1.5 text-xs text-agentic-text-muted">
                One plan with {pricingRows.length} pricing intervals — customers choose at checkout.
              </p>
            )}
            <p className="mt-1.5 text-xs text-agentic-text-muted">A price of 0 makes the plan free: tenants activate it without payment.</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Free trial (days)</label>
              <TextField
                className={inputCls}
                type="text"
                inputMode="numeric"
                value={trialDays}
                onChange={(e) => {
                  if (/^\d{0,3}$/.test(e.target.value)) setTrialDays(e.target.value);
                }}
              />
              <p className="mt-1 text-xs text-agentic-text-muted">
                0 = no trial. Card is taken at signup; first charge the day after the trial ends. One trial per tenant per pod.
              </p>
            </div>
          </div>

          {/* Plan Items (Features) */}
          <div className="rounded-lg border border-agentic-gray-200">
            <div className="flex items-center justify-between border-b border-agentic-gray-200 px-4 py-3">
              <div>
                <p className="text-sm font-semibold text-agentic-text-primary">Plan Items</p>
                <p className="text-xs text-agentic-text-muted">
                  {intervals.length > 1
                    ? 'Features included in this plan — set a value for each billing period'
                    : 'Features included in this plan'}
                </p>
              </div>
              <Button tone="primary-gradient"
                type="button"
                onClick={() => { setPickerSearch(''); setShowFeaturePicker(true); }}
                className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium transition-all"
              >
                <Plus className="h-3 w-3" />
                Add Item
              </Button>
            </div>

            {items.length === 0 ? (
              <p className="px-4 py-5 text-center text-sm text-agentic-text-muted">
                No plan items yet. Click "+ Add Item" to pick features.
              </p>
            ) : (
              <div className="divide-y divide-agentic-gray-100">
                {items.map((item) => (
                  <div key={item.featureId} className="flex items-center gap-3 px-4 py-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-agentic-text-primary">{item.feature.name}</p>
                      <p className="font-mono text-xs text-agentic-text-muted">{item.feature.key}</p>
                    </div>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${typeBadge[item.feature.type] ?? ''}`}>
                      {item.feature.type}
                    </span>
                    <div className="flex flex-wrap items-end justify-end gap-3">
                      {intervals.map((interval) => (
                        <div key={interval} className="flex flex-col items-end gap-1">
                          {intervals.length > 1 && (
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-agentic-text-muted">
                              {INTERVAL_LABEL[interval] ?? interval}
                            </span>
                          )}
                          <ValueInput feature={item.feature} value={valueFor(item, interval)} onChange={(v) => setItemValue(item.featureId, interval, v)} />
                        </div>
                      ))}
                    </div>
                    <Button tone="danger-ghost"
                      type="button"
                      onClick={() => setItems((prev) => prev.filter((i) => i.featureId !== item.featureId))}
                      className="shrink-0 rounded-lg p-1 transition-colors"
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </form>
      </Modal>

      {/* Feature picker */}
      <Modal
        isOpen={showFeaturePicker}
        onClose={() => setShowFeaturePicker(false)}
        title="Add Plan Item"
        size="sm"
      >
        <div className="space-y-3">
          <TextField
            autoFocus
            className={inputCls}
            placeholder="Search features…"
            value={pickerSearch}
            onChange={(e) => setPickerSearch(e.target.value)}
          />
          <div className="max-h-64 overflow-y-auto -mx-6 px-6">
            {pickerOptions.length === 0 ? (
              <p className="py-6 text-center text-sm text-agentic-text-muted">No features available</p>
            ) : (
              <div className="divide-y divide-agentic-gray-100">
                {pickerOptions.map((f) => (
                  <Button tone="custom"
                    key={f.id}
                    type="button"
                    onClick={() => addFeature(f)}
                    className="flex w-full items-center justify-between py-2.5 text-left hover:opacity-70 transition-opacity"
                  >
                    <div>
                      <p className="text-sm font-medium text-agentic-text-primary">{f.name}</p>
                      <p className="font-mono text-xs text-agentic-text-muted">{f.key}</p>
                    </div>
                    <span className={`ml-3 shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${typeBadge[f.type] ?? ''}`}>
                      {f.type}
                    </span>
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
};
